import type { AppData, BodyWeightEntry, Exercise, ScheduledWorkout, Settings, SyncMutation, WorkoutSession } from '../types.js';

function replaceById<T extends { id: string }>(items: T[], value: T): T[] {
  return items.some((item) => item.id === value.id)
    ? items.map((item) => item.id === value.id ? value : item)
    : [value, ...items];
}

/**
 * Start with the newest server snapshot and overlay only the entities that are
 * still waiting in this device's outbox. A single rejected local mutation must
 * not freeze every other workout and session at an old cached snapshot.
 */
export function mergeServerDataWithPending(server: AppData, pending: SyncMutation[]): AppData {
  let next: AppData = {
    ...server,
    settings: { ...server.settings },
    exercises: [...server.exercises],
    workouts: [...server.workouts],
    sessions: [...server.sessions],
    programs: [...server.programs],
    bodyWeights: [...server.bodyWeights],
    goals: [...server.goals],
    coachNotes: [...(server.coachNotes ?? [])],
  };

  for (const mutation of pending) {
    const payload = mutation.payload as { id?: unknown } | undefined;
    const id = typeof payload?.id === 'string' ? payload.id : undefined;
    switch (mutation.type) {
      case 'upsert_workout':
        if (id) next.workouts = replaceById(next.workouts, mutation.payload as ScheduledWorkout);
        break;
      case 'delete_workout':
        if (id) next.workouts = next.workouts.filter((item) => item.id !== id);
        break;
      case 'upsert_session': {
        if (!id) break;
        const session = mutation.payload as WorkoutSession;
        next.sessions = replaceById(next.sessions, session);
        if (session.scheduledWorkoutId) next.workouts = next.workouts.map((workout) => workout.id === session.scheduledWorkoutId ? {
          ...workout,
          status: session.status === 'completed' ? 'completed' : session.status === 'abandoned' ? 'planned' : 'started',
        } : workout);
        break;
      }
      case 'upsert_exercise':
        if (id) next.exercises = replaceById(next.exercises, mutation.payload as Exercise);
        break;
      case 'upsert_body_weight':
        if (id) next.bodyWeights = replaceById(next.bodyWeights, mutation.payload as BodyWeightEntry);
        break;
      case 'delete_body_weight':
        if (id) next.bodyWeights = next.bodyWeights.filter((item) => item.id !== id);
        break;
      case 'update_settings':
        next = { ...next, settings: mutation.payload as Settings };
        break;
      case 'dismiss_coach_note':
        if (id) next.coachNotes = (next.coachNotes ?? []).filter((item) => item.id !== id);
        break;
      case 'clone_program':
        break;
    }
  }
  return next;
}
